import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { fetchAllPages, indexPeople, personName, toPeople, withLiveTaskNames, type Person, type ProfileRow } from "@/lib/people";

/**
 * The shared, cached list of people (staff with a login). One query for every picker
 * and every live-name lookup — replaces the per-component `profiles` queries
 * (Phase 2, HDQ-TASKN-5). See src/lib/people.ts for the rules.
 */
export const PEOPLE_QUERY_KEY = ["people"] as const;

// PostgREST caps an unbounded select at 1000 rows SILENTLY, and .limit() does not raise
// it — only .range() paging does (NOTES-MES "POSTGREST ROW CAPS"). Page to exhaustion,
// ordered by full_name then the unique user_id so no page skips or repeats a row.
export const PEOPLE_PAGE_SIZE = 1000;

export async function fetchPeople(): Promise<Person[]> {
  const rows = await fetchAllPages<ProfileRow>(async (from, to) => {
    const { data, error } = await supabase
      .from("profiles")
      .select("user_id, full_name, email, is_test_account, avatar_url")
      .not("user_id", "is", null)
      .order("full_name")
      .order("user_id")
      .range(from, to);
    if (error) throw error;
    return (data ?? []) as ProfileRow[];
  }, PEOPLE_PAGE_SIZE);
  return toPeople(rows);
}

export const peopleQueryOptions = {
  queryKey: PEOPLE_QUERY_KEY,
  queryFn: fetchPeople,
  // Names change rarely; share one copy across the app for five minutes.
  staleTime: 5 * 60 * 1000,
  // staleTime alone never refetches (the app disables window-focus refetch, and nothing
  // invalidates this key on a profile edit), so a mounted screen would show a renamed
  // person's old name indefinitely. Re-read every five minutes while it is open.
  refetchInterval: 5 * 60 * 1000,
};

export interface UsePeopleResult {
  people: Person[];
  byId: Map<string, Person>;
  isLoading: boolean;
  isError: boolean;
}

export function usePeople(enabled = true): UsePeopleResult {
  const query = useQuery({ ...peopleQueryOptions, enabled });
  const people = useMemo(() => query.data ?? [], [query.data]);
  const byId = useMemo(() => indexPeople(people), [people]);
  return { people, byId, isLoading: query.isLoading, isError: query.isError };
}

/**
 * A React Query `select` that swaps copied task names for people's CURRENT names.
 * Pass it to a task query: `useQuery({ ..., select: useLiveTaskNames() })`. Until the
 * people list loads (or if it fails) rows keep their stored names, so a task screen
 * never breaks or blanks because of it.
 */
export function useLiveTaskNames<T extends Parameters<typeof withLiveTaskNames>[0]>(): (rows: T[]) => T[] {
  const { byId } = usePeople();
  return useCallback((rows: T[]) => (byId.size === 0 ? rows : rows.map((r) => withLiveTaskNames(r, byId))), [byId]);
}

/**
 * `(loginId, storedName) => currentName`. For screens that show a copied person name
 * (npd_formulations.assigned_to_name, ncrs.assigned_to_name, suppliers' owner, …):
 * the CURRENT name for the login id wins; the stored copy is only the fallback while the
 * people list loads, if it fails, or for a login that no longer exists.
 */
export function usePersonName(): (userId: string | null | undefined, fallback: string | null | undefined) => string | null {
  const { byId } = usePeople();
  return useCallback(
    (userId: string | null | undefined, fallback: string | null | undefined) => personName(byId, userId, fallback),
    [byId],
  );
}
